/**
 * Search Agent
 * Manages property search functionality including semantic and advanced search.
 */

import { useState, useCallback } from 'react';
import APIAgent from './apiAgent';

/**
 * Custom hook for advanced property search with filters
 * @returns {Object} Search state and methods
 */
export const useAdvancedSearch = (initialFilters = {}) => {
  const safeFilters = initialFilters || {};
  const [filters, setFilters] = useState({
    city: safeFilters.city || '',
    state: safeFilters.state || '',
    zip_code: safeFilters.zip_code || '',
    property_type: safeFilters.property_type || '',
    min_price: safeFilters.min_price ?? null,
    max_price: safeFilters.max_price ?? null,
    min_bedrooms: safeFilters.min_bedrooms ?? null,
    max_bedrooms: safeFilters.max_bedrooms ?? null,
    min_bathrooms: safeFilters.min_bathrooms ?? null,
    max_bathrooms: safeFilters.max_bathrooms ?? null,
    min_sqft: safeFilters.min_sqft ?? null,
    max_sqft: safeFilters.max_sqft ?? null,
    year_built_after: safeFilters.year_built_after ?? null,
    year_built_before: safeFilters.year_built_before ?? null,
    sort_by: safeFilters.sort_by || 'price',
    sort_order: safeFilters.sort_order || 'asc',
    limit: 20,
    offset: 0,
  });

  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Execute advanced search with current or updated filters
   * @param {Object} updatedFilters - Partial filter updates
   */
  const executeSearch = useCallback(async (updatedFilters = {}) => {
    setIsSearching(true);
    setError(null);

    try {
      const searchFilters = { ...filters, ...updatedFilters };
      
      // Remove null/empty values
      const cleanFilters = Object.fromEntries(
        Object.entries(searchFilters).filter(([_, v]) => v !== null && v !== '')
      );

      const response = await APIAgent.request(
        '/api/search/advanced',
        'POST',
        cleanFilters
      );

      setResults(response);
    } catch (err) {
      console.error('Advanced search failed:', err);
      setError(err.message || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  }, [filters]);

  /**
   * Update filter state
   * @param {string} key - Filter key
   * @param {*} value - Filter value
   */
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  /**
   * Update multiple filters at once
   * @param {Object} updates - Filter updates
   */
  const updateFilters = useCallback((updates) => {
    setFilters((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  /**
   * Reset filters to defaults
   */
  const resetFilters = useCallback(() => {
    setFilters({
      city: '',
      state: '',
      zip_code: '',
      min_price: null,
      max_price: null,
      min_bedrooms: null,
      max_bedrooms: null,
      min_bathrooms: null,
      max_bathrooms: null,
      min_sqft: null,
      max_sqft: null,
      year_built_after: null,
      year_built_before: null,
      sort_by: 'price',
      sort_order: 'asc',
      limit: 20,
      offset: 0,
    });
    setResults(null);
    setError(null);
  }, []);

  /**
   * Paginate to next page
   */
  const nextPage = useCallback(() => {
    executeSearch({
      offset: filters.offset + filters.limit,
    });
    setFilters((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  }, [filters.limit, filters.offset, executeSearch]);

  /**
   * Paginate to previous page
   */
  const previousPage = useCallback(() => {
    if (filters.offset > 0) {
      const newOffset = Math.max(0, filters.offset - filters.limit);
      executeSearch({
        offset: newOffset,
      });
      setFilters((prev) => ({
        ...prev,
        offset: newOffset,
      }));
    }
  }, [filters.limit, filters.offset, executeSearch]);

  return {
    filters,
    results,
    isSearching,
    error,
    updateFilter,
    updateFilters,
    executeSearch,
    resetFilters,
    nextPage,
    previousPage,
  };
};

/**
 * Custom hook for semantic search with embedding-based matching
 * @returns {Object} Semantic search state and methods
 */
export const useSemanticSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Execute semantic search
   * @param {string} searchQuery - Search query text
   * @param {Object} options - Search options (search_type, limit, threshold)
   */
  const executeSearch = useCallback(async (searchQuery, options = {}) => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      setError('Search query cannot be empty');
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const payload = {
        query: searchQuery,
        search_type: options.search_type || null,
        limit: options.limit || 10,
        threshold: options.threshold || 0.5,
      };

      const response = await APIAgent.request(
        '/api/search/semantic',
        'POST',
        payload,
        {},
        { timeout: 60000 }
      );

      setResults(response);
      setQuery(searchQuery);
    } catch (err) {
      console.error('Semantic search failed:', err);
      setError(err.message || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  }, []);

  /**
   * Clear search results
   */
  const clearResults = useCallback(() => {
    setResults(null);
    setQuery('');
    setError(null);
  }, []);

  return {
    query,
    results,
    isSearching,
    error,
    executeSearch,
    clearResults,
  };
};
